import {
  adminRoles,
  allRoles,
  capstoneModuleRoles,
  cohortReadRoles,
  reportViewRoles,
  resourceModuleRoles,
  writeOperationsRoles,
  type AppRouteDefinition
} from "./route-types";

export const workflowRoutes: AppRouteDefinition[] = [
  {
    id: "meetings",
    path: "/meetings",
    label: "Meetings",
    capability: "sessions:view",
    roles: allRoles,
    directUrlPolicy: "allow",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    // Wave 6: the operational Sessions schedule. PARTICIPANT is denied at the
    // ROUTE level (cohortReadRoles excludes participant -> generic-not-found + no
    // Sessions nav) AND at the PAGE level (fail-closed guard) — defense in depth.
    // cohort_lead is included for assigned-cohort READ.
    id: "sessions",
    path: "/sessions",
    label: "Sessions",
    capability: "sessions:view",
    roles: cohortReadRoles,
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    // Wave 6: the bounded date-range Calendar view (same scoped session read as
    // the list). Participant denied at the ROUTE (cohortReadRoles) AND PAGE level.
    id: "calendar",
    path: "/calendar",
    label: "Calendar",
    capability: "sessions:view",
    roles: cohortReadRoles,
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    id: "cohort-session-series",
    path: "/cohorts/:cohort_id/sessions/series",
    label: "Create Session Series",
    capability: "sessions:create",
    roles: writeOperationsRoles,
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "cohort",
    scopeParam: "cohort_id"
  },
  {
    // Clear Series (FR-SESS-002) — the backend POST /cohorts/:id/sessions/clear is
    // NOW LIVE and granted to super_admin + coach only (migration 0227). So the
    // ROUTE is reachable by SA + coach only (admin/company_admin/cohort_lead/
    // participant -> generic-not-found, matching the backend deny); the page also
    // resolves the sessions:clear-series capability (SA + coach) and requires an
    // explicit destructive confirm + a UUID Idempotency-Key before anything clears.
    id: "clear-series",
    path: "/cohorts/:cohort_id/sessions/clear",
    label: "Clear Session Series",
    capability: "sessions:create",
    roles: ["super_admin", "coach"],
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "cohort",
    scopeParam: "cohort_id"
  },
  {
    // Wave 6: session detail keeps allRoles at the route (preserving the
    // record-scope resolution chain — the gold-pattern cohort-workspace recipe),
    // but the PAGE fail-closes for participant (canAccessSessionsModule). So a
    // participant who reaches the component sees only permission-denied with NO
    // session; their own sessions are the Wave-9 dashboard. cohort_lead reads.
    id: "session-detail",
    path: "/sessions/:session_id",
    label: "Session Detail",
    capability: "sessions:view",
    roles: allRoles,
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "session",
    scopeParam: "session_id"
  },
  {
    id: "session-invites",
    path: "/sessions/:session_id/invites",
    label: "Send Invites",
    capability: "sessions:invite",
    roles: writeOperationsRoles,
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "session",
    scopeParam: "session_id"
  },
  {
    id: "session-reschedule",
    path: "/sessions/:session_id/reschedule",
    label: "Reschedule Session",
    capability: "sessions:reschedule",
    roles: writeOperationsRoles,
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "session",
    scopeParam: "session_id"
  },
  {
    // Wave 7 (Reports). FR-REPORT-001: participant is EXCLUDED at the route
    // (reportViewRoles, not allRoles) → generic-not-found on a direct URL + no
    // nav. The page guard (canAccessReportsModule) fail-closes belt-and-
    // suspenders. A participant must never learn a report even EXISTS.
    id: "reports",
    path: "/reports",
    label: "Reports",
    capability: "reports:view",
    roles: reportViewRoles,
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    id: "report-detail",
    path: "/reports/:report_id",
    label: "Report Detail",
    capability: "reports:view",
    roles: reportViewRoles,
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "report",
    scopeParam: "report_id"
  },
  {
    id: "session-report",
    path: "/sessions/:session_id/report",
    label: "Session Report",
    capability: "reports:view",
    roles: reportViewRoles,
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "session",
    scopeParam: "session_id"
  },
  {
    id: "transcript-detail",
    path: "/sessions/:session_id/transcript",
    label: "Transcript Detail",
    capability: "reports:view",
    roles: ["super_admin", "admin", "coach", "company_admin"],
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "session",
    scopeParam: "session_id"
  },
  {
    // Wave 8 (Resources). PARTICIPANT is DENIED the admin Resources module at
    // the ROUTE (resourceModuleRoles excludes participant → generic-not-found +
    // no nav) AND at the PAGE (canAccessResourcesModule fail-closed). This
    // REPLACES the prior allRoles/allow posture: a participant's resources
    // context is the Wave-9 dashboard, not this admin surface.
    id: "resources",
    path: "/resources",
    label: "Resources",
    capability: "resources:view",
    roles: resourceModuleRoles,
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    id: "resource-detail",
    path: "/resources/:resource_id",
    label: "Resource Detail",
    capability: "resources:view",
    roles: resourceModuleRoles,
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "resource",
    scopeParam: "resource_id"
  },
  {
    // Wave 8 (Capstone — admin). PARTICIPANT DENIED (route + page fail-closed).
    // The ONLY backend capstone route is the STAFF-scoped intake
    // (POST /cohorts/:id/capstone-submissions); there is NO participant submit
    // route (Wave-9 backend gap) and NO capstone list/get route (the admin view
    // is honest until a read route lands). The agent-eval lane is dark.
    id: "capstone",
    path: "/capstone",
    label: "Capstone",
    capability: "capstone:view",
    roles: capstoneModuleRoles,
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    id: "capstone-submissions",
    path: "/capstone/submissions",
    label: "Capstone Submissions",
    capability: "capstone:view",
    roles: capstoneModuleRoles,
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    // Certificates use the live GET/POST readiness contract. The page keeps
    // staff authorization, pending artifactRef honesty, and runtime error
    // postures; participant and non-staff roles remain denied at the route.
    id: "certificates",
    path: "/certificates",
    label: "Certificates",
    capability: "certificates:view",
    roles: adminRoles,
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    id: "history",
    path: "/history",
    label: "History",
    capability: "history:view",
    roles: ["super_admin", "admin", "coach", "company_admin", "cohort_lead"],
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    id: "users",
    path: "/users",
    label: "Users",
    capability: "users:view",
    roles: adminRoles,
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    id: "user-detail",
    path: "/users/:user_id",
    label: "User Detail",
    capability: "users:view",
    roles: adminRoles,
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "user",
    scopeParam: "user_id"
  },
  {
    id: "user-edit",
    path: "/users/:user_id/edit",
    label: "Edit User",
    capability: "users:view",
    roles: adminRoles,
    directUrlPolicy: "generic-not-found",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "user",
    scopeParam: "user_id"
  },
  {
    id: "permissions",
    path: "/permissions",
    label: "Permissions",
    capability: "permissions:view",
    roles: ["super_admin"],
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "blocked-backend",
    scopeKind: "none",
    featureGate: "super-admin-permission-management-backend"
  },
  {
    // Wave 10 (Exports). The CSV export backend (GET /exports/:export_type,
    // text/csv, 9 types, per-type view permission) IS live — so this is now a
    // real planned-screen, not a backend-blocked placeholder. PARTICIPANT is
    // DENIED at the ROUTE (generic-not-found + no nav) AND at the PAGE
    // (canAccessExportsModule fail-closed). cohort_lead is added (assigned-scope
    // export ≤ its view). The export type list each role sees is scoped to its
    // view (export ≤ view, frozen at request).
    id: "exports",
    path: "/exports",
    label: "Exports",
    capability: "exports:view",
    roles: ["super_admin", "admin", "coach", "company_admin", "cohort_lead"],
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    id: "settings",
    path: "/settings",
    label: "Settings",
    capability: "settings:view",
    roles: allRoles,
    directUrlPolicy: "allow",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    id: "profile",
    path: "/settings/profile",
    label: "Profile",
    capability: "settings:view",
    roles: allRoles,
    directUrlPolicy: "allow",
    nav: false,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    // Settings/Thresholds uses the live GET/POST readiness contract. Missing
    // rows are rendered as not set; denied, unavailable, and error states are
    // honest runtime postures. STAFF/coach reach; PARTICIPANT is denied.
    id: "settings-thresholds",
    path: "/settings/thresholds",
    label: "Thresholds",
    capability: "settings:view",
    roles: ["super_admin", "admin", "coach"],
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  },
  {
    // Tool View uses the live super-admin-only GET /tools/diagnostics read. It
    // remains read-only and secret-safe; denied and outage responses stay
    // honest rather than turning into fabricated diagnostics.
    id: "tools",
    path: "/tools",
    label: "Tool View",
    capability: "tools:view",
    roles: ["super_admin"],
    directUrlPolicy: "generic-not-found",
    nav: true,
    readiness: "planned-screen",
    scopeKind: "none"
  }
];
